"use client";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  ChevronDown,
  Droplets,
  Flame,
  Info,
  Thermometer,
  Wind,
} from "lucide-react";
import { useState } from "react";

interface ClimateRisksProps {
  climateData?: {
    flood?: {
      score: number;
      scoreText: string;
      propertiesAtRisk: number;
      percentagePropertiesAtRisk: number;
    };
    fire?: {
      score: number;
      scoreText: string;
      propertiesAtRisk: number;
      percentagePropertiesAtRisk: number;
    };
    heat?: {
      score: number;
      scoreText: string;
      propertiesAtRisk: number;
      percentagePropertiesAtRisk: number;
      hotTemperature?: number;
      hotDaysToday?: number;
      hotDaysFuture?: number;
    };
    wind?: {
      score: number;
      scoreText: string;
      propertiesAtRisk: number;
      percentagePropertiesAtRisk: number;
      greatestWindRisk?: string;
    };
  };
}

interface RiskData {
  type: string;
  icon: JSX.Element;
  score: number;
  severity: "minimal" | "moderate" | "major" | "severe";
  label: string;
  percent: number;
  details: string[];
}

const RISK_CONFIG = {
  minimal: {
    color: "text-emerald-600",
    bg: "bg-emerald-50/50",
    label: "Minimal Risk",
    badge: "bg-emerald-50 text-emerald-700",
  },
  moderate: {
    color: "text-amber-600",
    bg: "bg-amber-50/50",
    label: "Moderate Risk",
    badge: "bg-amber-50 text-amber-700",
  },
  major: {
    color: "text-orange-600",
    bg: "bg-orange-50/50",
    label: "Major Risk",
    badge: "bg-orange-50 text-orange-700",
  },
  severe: {
    color: "text-red-600",
    bg: "bg-red-50/50",
    label: "Severe Risk",
    badge: "bg-red-50 text-red-700",
  },
};

function getRiskSeverity(
  score: number,
): "minimal" | "moderate" | "major" | "severe" {
  if (score <= 1) return "minimal";
  if (score <= 3) return "moderate";
  if (score <= 6) return "major";
  return "severe";
}

function formatPercentage(value: number): string {
  if (value < 0.01) return "<1%";
  if (value >= 0.01) return `${Math.round(value * 100)}%`;
  return "0%";
}

function RiskCard({ risk }: { risk: RiskData }) {
  const [expanded, setExpanded] = useState(false);
  const config = RISK_CONFIG[risk.severity];

  return (
    <div
      className={cn(
        "rounded-lg border border-gray-200 overflow-hidden transition-all group",
        expanded ? "shadow-sm" : "",
      )}
    >
      <button
        type="button"
        className={cn(
          "w-full p-4 text-left cursor-pointer transition-colors hover:bg-gray-50/80",
          expanded && "bg-gray-50/50",
        )}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setExpanded(!expanded);
          }
        }}
        aria-expanded={expanded}
        aria-controls={`${risk.type}-details`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={cn("p-2 rounded-full bg-gray-100", config.color)}>
              {risk.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold capitalize">{risk.type}</h4>
              <p className="text-sm text-muted-foreground truncate">
                {formatPercentage(risk.percent)} of properties affected
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-3 mt-2 sm:mt-0">
            <div
              className={cn(
                "text-sm font-medium",
                config.badge,
                "px-2.5 py-1 rounded-full",
                "inline-flex items-center justify-center",
              )}
            >
              {config.label}
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <span className="hidden group-hover:inline">
                {expanded ? "Collapse" : "Expand"}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  expanded && "rotate-180",
                )}
              />
            </div>
          </div>
        </div>
      </button>

      {expanded && risk.details.length > 0 && (
        <motion.div
          id={`${risk.type}-details`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="px-4 pb-4 space-y-2"
        >
          <div className="w-full h-px bg-gray-100" />
          <div className="space-y-2">
            {risk.details.map((detail, index) => (
              <p
                key={`${risk.type}-detail-${index}`}
                className="text-sm text-muted-foreground"
              >
                {detail}
              </p>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

function ClimateRiskSummary({ risks }: { risks: RiskData[] }) {
  const severeCounts = risks.filter((r) => r.severity === "severe").length;
  const majorCounts = risks.filter((r) => r.severity === "major").length;
  const moderateCounts = risks.filter((r) => r.severity === "moderate").length;

  let overallRisk: "minimal" | "moderate" | "major" | "severe" = "minimal";
  if (severeCounts > 0) overallRisk = "severe";
  else if (majorCounts > 0) overallRisk = "major";
  else if (moderateCounts > 0) overallRisk = "moderate";

  const config = RISK_CONFIG[overallRisk];

  return (
    <div className="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-semibold text-lg">
            {overallRisk === "minimal"
              ? "Low Climate Risk"
              : `${config.label} Profile`}
          </h3>
          <p className="text-sm text-muted-foreground">
            {overallRisk === "minimal"
              ? "This property has minimal climate-related risks"
              : `This property has ${severeCounts} severe, ${majorCounts} major, and ${moderateCounts} moderate risk factors`}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          {risks.map((risk) => (
            <div
              key={risk.type}
              className={cn(
                "p-1.5 rounded-full bg-gray-100",
                RISK_CONFIG[risk.severity].color,
              )}
              title={`${risk.type}: ${RISK_CONFIG[risk.severity].label}`}
            >
              {risk.icon}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ClimateRisks({ climateData }: ClimateRisksProps) {
  if (!climateData) return null;

  const risks: RiskData[] = [];

  if (climateData.flood) {
    const severity = getRiskSeverity(climateData.flood.score);
    const details = [];

    if (climateData.flood.percentagePropertiesAtRisk > 0.01) {
      details.push(
        `${formatPercentage(climateData.flood.percentagePropertiesAtRisk)} of homes in this area (${climateData.flood.propertiesAtRisk.toLocaleString()} properties) have some flood risk.`,
      );
    }

    if (severity !== "minimal") {
      details.push(
        "Consider researching flood insurance options and preventive measures like proper drainage systems.",
      );
    }

    risks.push({
      type: "flood",
      icon: <Droplets className="h-4 w-4" />,
      score: climateData.flood.score,
      severity,
      label: climateData.flood.scoreText,
      percent: climateData.flood.percentagePropertiesAtRisk,
      details,
    });
  }

  if (climateData.fire) {
    const severity = getRiskSeverity(climateData.fire.score);
    const details = [];

    if (climateData.fire.percentagePropertiesAtRisk > 0.05) {
      details.push(
        `${formatPercentage(climateData.fire.percentagePropertiesAtRisk)} of homes in this area (${climateData.fire.propertiesAtRisk.toLocaleString()} properties) have wildfire risk.`,
      );
    }

    if (severity !== "minimal") {
      details.push(
        "Consider fire-resistant building materials, maintaining defensible space, and reviewing insurance coverage.",
      );
    }

    risks.push({
      type: "fire",
      icon: <Flame className="h-4 w-4" />,
      score: climateData.fire.score,
      severity,
      label: climateData.fire.scoreText,
      percent: climateData.fire.percentagePropertiesAtRisk,
      details,
    });
  }

  if (climateData.heat) {
    const severity = getRiskSeverity(climateData.heat.score);
    const details = [];

    if (climateData.heat.hotTemperature && climateData.heat.hotDaysToday) {
      details.push(
        `This area experiences approximately ${climateData.heat.hotDaysToday} days per year with temperatures above ${climateData.heat.hotTemperature}°F.`,
      );
    }

    if (
      climateData.heat.hotDaysFuture &&
      climateData.heat.hotDaysToday &&
      climateData.heat.hotDaysFuture > climateData.heat.hotDaysToday
    ) {
      details.push(
        `Extreme heat days are projected to increase from ${climateData.heat.hotDaysToday} to ${climateData.heat.hotDaysFuture} days per year in the coming decades.`,
      );
    }

    if (severity !== "minimal") {
      details.push(
        "Consider energy-efficient cooling systems, insulation quality, and window treatments to manage heat.",
      );
    }

    risks.push({
      type: "heat",
      icon: <Thermometer className="h-4 w-4" />,
      score: climateData.heat.score,
      severity,
      label: climateData.heat.scoreText,
      percent: climateData.heat.percentagePropertiesAtRisk,
      details,
    });
  }

  if (climateData.wind) {
    const severity = getRiskSeverity(climateData.wind.score);
    const details = [];

    if (climateData.wind.greatestWindRisk) {
      details.push(
        `The main wind hazard in this area is ${climateData.wind.greatestWindRisk}.`,
      );
    }

    if (severity !== "minimal") {
      details.push(
        "Consider the roof condition, tree proximity, and storm shutters or impact-resistant windows.",
      );
    }

    risks.push({
      type: "wind",
      icon: <Wind className="h-4 w-4" />,
      score: climateData.wind.score,
      severity,
      label: climateData.wind.scoreText,
      percent: climateData.wind.percentagePropertiesAtRisk,
      details,
    });
  }

  // Sort risks by severity (highest score first)
  const sortedRisks = [...risks].sort((a, b) => b.score - a.score);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Climate Risks</h3>
          <p className="text-sm text-muted-foreground">
            Assessment of climate-related hazards for this property
          </p>
        </div>

        <ClimateRiskSummary risks={sortedRisks} />

        <div className="space-y-3">
          {sortedRisks.map((risk) => (
            <RiskCard key={risk.type} risk={risk} />
          ))}
        </div>

        <div className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <p>
            Climate risk data is based on historical patterns and projections.
            Consider consulting with insurance providers and local experts for
            property-specific assessments.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
